import React from 'react';
import {useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import {
  createSpareRequisition,
  createSpareRequisitionTemplate,
  importSpareRequisitionFromExcel,
  peekSpareReference,
  updateSpareRequisition,
} from '../../../services/mutations';
import { spareRequisitionRefPeekSnapshot } from '../peekRefSnapshot';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  ChevronLeftIcon,
  DateInput,
  DocumentTextIcon,
  EyeIcon,
  FolderPlusIcon,
  Label,
  NumberInput,
  PlusIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
  UploadCloudIcon,
  MultiSelect,
} from '../../../components/ui';

import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { SpareItemDetailModal } from '../spare-items/SpareItemDetailModal';
import { SpareItemPickerModal } from '../spare-items/SpareItemPickerModal';
import type { SpareItem } from '../spare-items/useData';
import { isSpareRequisitionLockedAfterPipelineStep, moduleConfig } from './requisitionsModuleConfig';
import {
  SpareRequisitionLine,
  SpareRequisitionTemplate,
  useSpareRequisition,
} from './useData';
import { formatCaseParenthetical } from './caseDisplay';

type LineForm = {
  key: string;
  itemId: string;
  itemVcode: string;
  itemDescr: string;
  qty: string;
  itemNotes: string;
};

const newLine = (): LineForm => ({
  key: `line-${Date.now()}-${Math.random()}`,
  itemId: '',
  itemVcode: '',
  itemDescr: '',
  qty: '',
  itemNotes: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

/** Qty to carry into a new requisition for delivery shortfalls / not marked received (see supply delivery line import). */
function followUpQtyFromDeliveryLine(line: {
  purchaseOrderQty?: number | null;
  deliveryQty?: number | null;
  isDelivered?: boolean;
}): number | null {
  const po = line.purchaseOrderQty;
  const d = line.deliveryQty;
  if (line.isDelivered === false) {
    if (po != null && d != null) {
      const rem = po - d;
      if (rem > 0) return rem;
      if (rem === 0) return po;
    }
    if (po != null) return po;
    if (d != null) return d;
    return null;
  }
  if (line.isDelivered === true && po != null && d != null && po !== d) {
    return Math.abs(po - d);
  }
  return null;
}

/** `YYYY-MM-DD` in local timezone (for date inputs). */
const getTodayLocalDateString = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Single-line label: `vendorCode - name` with optional drawing. */
function formatRequisitionItemLabel(
  vendorCode: string | null | undefined,
  name: string | null | undefined,
  drawingNo?: string | null,
): string {
  const v = (vendorCode ?? '').trim() || '';
  const n = (name ?? '').trim() || '';
  const base = `${v} - ${n}`;
  const d = (drawingNo ?? '').trim();
  return d ? `${base} · Drw ${d}` : base;
}

const Form: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack(
    moduleConfig.module.route.list,
    getLocationReturnPath(location.state),
  );
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const requisitionId = id ? Number(id) : undefined;
  const caseIdFromQuery = searchParams.get('caseId');
  const vesselIdFromQuery = searchParams.get('vesselId');
  const importFromQuery = searchParams.get('importFrom');
  const importFromDeliveryQuery = searchParams.get('importFromDelivery');
  const noCaseFromQuery = searchParams.get('noCase') === '1' || searchParams.get('noCase') === 'true';
  const prefillCaseId =
    !isEdit && !noCaseFromQuery && caseIdFromQuery && /^\d+$/.test(caseIdFromQuery) ? caseIdFromQuery : null;
  const lockCaseContext = Boolean(prefillCaseId);
  const lockNoCase = !isEdit && noCaseFromQuery;
  const { data: supplyCaseForPrefill } = useSWR<{
    id: number;
    ref?: string | null;
    vesselId?: number | null;
    vessel?: { id: number; vesselName?: string | null } | null;
  } | null>(prefillCaseId && !noCaseFromQuery ? `/spare-cases/${prefillCaseId}` : null, fetcher);

  const { data: refTemplates } = useSWR<
    Array<{ entityType: string; allowManualReferenceEdit?: boolean; isActive?: boolean; pattern?: string }>
  >('/reference-templates?moduleId=spares', fetcher, { revalidateOnFocus: false });
  const allowManualRequisitionRef =
    refTemplates?.find((r) => r.entityType === 'SPARE_REQUISITION')?.allowManualReferenceEdit !== false;
  const hasActiveRequisitionTemplate = Boolean(
    refTemplates?.some(
      (r) =>
        r.entityType === 'SPARE_REQUISITION' &&
        r.isActive &&
        typeof r.pattern === 'string' &&
        r.pattern.trim().length > 0,
    ),
  );

  const { data: existing, isLoading: loadingExisting } = useSpareRequisition(requisitionId);

  React.useEffect(() => {
    if (!isEdit || !existing?.id) return;
    if (isSpareRequisitionLockedAfterPipelineStep(existing)) {
      navigate(moduleConfig.module.route.detail(existing.id), { replace: true });
    }
  }, [isEdit, existing, navigate]);

  const { data: casesRes } = useSWR<any>('/spare-cases?page=1&limit=300', fetcher);
  const cases = React.useMemo(
    () => (Array.isArray(casesRes) ? casesRes : casesRes?.data ?? []),
    [casesRes],
  );
  const { data: vessels = [] } = useSWR<any[]>('/vessels', fetcher);
  const { data: lineTemplates = [] } = useSWR<SpareRequisitionTemplate[]>(
    '/spare-requisition-templates',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: storeCategoriesRaw } = useSWR<
    | Array<{ id: number; name: string; abrv?: string | null }>
    | { data?: Array<{ id: number; name: string; abrv?: string | null }> }
  >('/spare-categories?page=1&limit=5000', fetcher, { revalidateOnFocus: false });
  const storeCategories = React.useMemo(
    () => (Array.isArray(storeCategoriesRaw) ? storeCategoriesRaw : storeCategoriesRaw?.data ?? []),
    [storeCategoriesRaw],
  );
  const categoryMultiOptions = React.useMemo(
    () =>
      storeCategories.map((c) => ({
        value: c.id,
        label: c.abrv ? `${c.name} (${c.abrv})` : c.name,
      })),
    [storeCategories],
  );
  const [lines, setLines] = React.useState<LineForm[]>([newLine()]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [templateApplyId, setTemplateApplyId] = React.useState('');
  const [saveTemplateOpen, setSaveTemplateOpen] = React.useState(false);
  const [deleteAllLinesOpen, setDeleteAllLinesOpen] = React.useState(false);
  const [importExcelBusy, setImportExcelBusy] = React.useState(false);
  const excelImportInputRef = React.useRef<HTMLInputElement>(null);
  const reqRefPeekTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  type HeaderForm = {
    reqDate: string;
    caseId: string;
    vesselId: string;
    categoryIds: number[];
    ref: string;
    refN: string;
    notes: string;
  };

  const [formData, setFormData] = React.useState<HeaderForm>(() => ({
    reqDate: id ? '' : getTodayLocalDateString(),
    caseId: '',
    vesselId: '',
    categoryIds: [],
    ref: '',
    refN: '',
    notes: '',
  }));

  React.useEffect(() => {
    if (!existing) return;
    const catIds =
      Array.isArray(existing.categoryIds) && existing.categoryIds.length > 0
        ? [...existing.categoryIds]
        : (existing.categories?.map((c) => c.id).filter((id): id is number => typeof id === 'number') ?? []);
    setFormData({
      reqDate: existing.reqDate ? existing.reqDate.slice(0, 10) : '',
      caseId: existing.caseId ? String(existing.caseId) : '',
      vesselId: existing.vesselId ? String(existing.vesselId) : '',
      categoryIds: catIds,
      ref: existing.ref || '',
      refN: existing.refN !== null && existing.refN !== undefined ? String(existing.refN) : '',
      notes: existing.notes || '',
    });

    const mapped = (existing.lines || []).map((line) => ({
      key: `line-${line.id || Math.random()}`,
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      qty: line.qty !== null && line.qty !== undefined ? String(line.qty) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped.length > 0 ? mapped : [newLine()]);
  }, [existing]);

  React.useEffect(() => {
    if (isEdit || !prefillCaseId) return;
    setFormData((s) => (s.caseId === prefillCaseId ? s : { ...s, caseId: prefillCaseId }));
  }, [isEdit, prefillCaseId]);

  React.useEffect(() => {
    if (isEdit || !supplyCaseForPrefill || noCaseFromQuery) return;
    setFormData((s) => ({
      ...s,
      caseId: String(supplyCaseForPrefill.id),
      vesselId:
        supplyCaseForPrefill.vesselId != null && supplyCaseForPrefill.vesselId !== undefined
          ? String(supplyCaseForPrefill.vesselId)
          : '',
    }));
  }, [isEdit, supplyCaseForPrefill, noCaseFromQuery]);

  React.useEffect(() => {
    if (isEdit || lockCaseContext) return;
    const queryV = vesselIdFromQuery && /^\d+$/.test(vesselIdFromQuery) ? vesselIdFromQuery : null;
    if (!queryV) return;
    setFormData((s) => (s.vesselId ? s : { ...s, vesselId: queryV }));
  }, [isEdit, vesselIdFromQuery, lockCaseContext]);

  React.useEffect(() => {
    if (isEdit || !lockNoCase) return;
    setFormData((s) => (s.caseId === '' ? s : { ...s, caseId: '' }));
  }, [isEdit, lockNoCase]);

  React.useEffect(() => {
    if (!isEdit) {
      spareRequisitionRefPeekSnapshot.reset();
    }
  }, [isEdit]);

  /** Preview next reference (peek does not consume seq). Omit unchanged value on create so the server allocates. */
  React.useEffect(() => {
    if (isEdit) return;

    const reqDate = formData.reqDate;
    const vesselRaw = formData.vesselId;
    const categoryIds = formData.categoryIds ?? [];
    const caseRaw = formData.caseId?.trim();

    if (reqRefPeekTimerRef.current) clearTimeout(reqRefPeekTimerRef.current);
    reqRefPeekTimerRef.current = setTimeout(() => {
      reqRefPeekTimerRef.current = undefined;
      void (async () => {
        if (!reqDate) return;
        const vesselId =
          vesselRaw === '' || vesselRaw === undefined || vesselRaw === null ? null : Number(vesselRaw);
        if (vesselId != null && !Number.isFinite(vesselId)) return;
        const cats = categoryIds.filter((id) => typeof id === 'number' && Number.isFinite(id));
        const caseId = caseRaw && /^\d+$/.test(caseRaw) ? Number(caseRaw) : undefined;

        try {
          const { ref } = await peekSpareReference({
            entityType: 'SPARE_REQUISITION',
            vesselId,
            categoryIds: cats,
            dateForYear: reqDate,
            caseId: caseId ?? null,
          });
          if (ref != null && ref.trim().length > 0) {
            const trimmed = ref.trim();
            setFormData((s) => {
              if (!spareRequisitionRefPeekSnapshot.shouldApplyPeekToForm(s.ref)) return s;
              spareRequisitionRefPeekSnapshot.setFromPeek(trimmed);
              return s.ref === trimmed ? s : { ...s, ref: trimmed };
            });
          }
        } catch {
          /* optional preview */
        }
      })();
    }, 200);

    return () => {
      if (reqRefPeekTimerRef.current) clearTimeout(reqRefPeekTimerRef.current);
    };
  }, [isEdit, formData.reqDate, formData.vesselId, formData.caseId, formData.categoryIds]);

  const importFromLinesLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (isEdit) return;
    if (!importFromQuery || !/^\d+$/.test(importFromQuery)) return;
    if (importFromLinesLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const full = (await fetcher(`/spare-requisitions/${importFromQuery}`)) as { lines?: SpareRequisitionLine[] };
        if (cancelled) return;
        const mapped: LineForm[] = (full.lines || []).map((line) => ({
          key: `line-${line.id ?? Date.now()}-${Math.random()}`,
          itemId: line.itemId != null ? String(line.itemId) : '',
          itemVcode: line.itemVcode || '',
          itemDescr: line.itemDescr || '',
          qty: line.qty !== null && line.qty !== undefined ? String(line.qty) : '',
          itemNotes: line.itemNotes || '',
        }));
        if (mapped.length > 0) {
          setLines(mapped);
          importFromLinesLoadedRef.current = true;
        }
      } catch {
        // keep default line row
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, importFromQuery]);

  const importFromDeliveryLinesLoadedRef = React.useRef(false);
  React.useEffect(() => {
    if (isEdit) return;
    if (!importFromDeliveryQuery || !/^\d+$/.test(importFromDeliveryQuery)) return;
    if (importFromDeliveryLinesLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const full = (await fetcher(`/spare-deliveries/${importFromDeliveryQuery}`)) as {
          ref?: string | null;
          id?: number;
          lines?: Array<{
            id?: number;
            itemId?: number | null;
            itemVcode?: string | null;
            itemDescription?: string | null;
            purchaseOrderQty?: number | null;
            deliveryQty?: number | null;
            isDelivered?: boolean;
            itemNotes?: string | null;
          }>;
        };
        if (cancelled) return;
        const dlvRef = (full.ref || '').trim() || `#${full.id ?? importFromDeliveryQuery}`;
        const noteLine = `Follow-up requisition: undelivered or qty mismatch from delivery ${dlvRef} (id ${importFromDeliveryQuery}).`;
        setFormData((s) => ({
          ...s,
          notes: s.notes?.trim() ? `${s.notes.trim()}\n\n${noteLine}` : noteLine,
        }));
        const mapped: LineForm[] = (full.lines || [])
          .map((line) => {
            const qtyN = followUpQtyFromDeliveryLine(line);
            if (qtyN == null || qtyN <= 0) return null;
            return {
              key: `dlv-line-${line.id ?? Date.now()}-${Math.random()}`,
              itemId: line.itemId != null ? String(line.itemId) : '',
              itemVcode: line.itemVcode || '',
              itemDescr: line.itemDescription || '',
              qty: String(qtyN),
              itemNotes: line.itemNotes
                ? `${line.itemNotes} (imported from delivery)`
                : 'Imported from delivery (shortfall / pending)',
            } as LineForm;
          })
          .filter((x): x is LineForm => x != null);
        if (mapped.length > 0) {
          setLines(mapped);
          importFromDeliveryLinesLoadedRef.current = true;
        }
      } catch {
        // keep default line row
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, importFromDeliveryQuery]);

  const [saveTemplateName, setSaveTemplateName] = React.useState('');
  const [saveTemplateBusy, setSaveTemplateBusy] = React.useState(false);
  const [pickerLineKey, setPickerLineKey] = React.useState<string | null>(null);
  const [viewItemId, setViewItemId] = React.useState<number | null>(null);
  const { data: itemsResponse } = useSWR<any>('/spare-items?page=1&limit=200', fetcher);
  const items = React.useMemo<any[]>(
    () => (Array.isArray(itemsResponse) ? itemsResponse : itemsResponse?.data ?? []),
    [itemsResponse],
  );
  const itemsById = React.useMemo(() => {
    const map = new Map<string, any>();
    for (const item of items) {
      map.set(String(item.id), item);
    }
    return map;
  }, [items]);
  const pickerOpen = pickerLineKey !== null;

  const templateSelectOptions = React.useMemo(
    () => [
      { value: '', label: 'Line template…' },
      ...lineTemplates.map((t) => ({ value: String(t.id), label: t.name })),
    ],
    [lineTemplates],
  );

  const applyLineTemplate = React.useCallback(() => {
    if (!templateApplyId) return;
    const t = lineTemplates.find((x) => String(x.id) === templateApplyId);
    if (!t?.lines?.length) return;
    const mapped: LineForm[] = t.lines.map((l) => ({
      ...newLine(),
      itemId: l.itemId != null ? String(l.itemId) : '',
      itemVcode: l.itemVcode || '',
      itemDescr: l.itemDescr || '',
      qty: l.qty !== null && l.qty !== undefined ? String(l.qty) : '',
      itemNotes: l.itemNotes || '',
    }));
    setLines((prev) => [...prev, ...mapped]);
  }, [lineTemplates, templateApplyId]);

  const handleExcelImportChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      setImportExcelBusy(true);
      try {
        const data = await importSpareRequisitionFromExcel(file);
        const importedRef = data.ref?.trim();
        if (importedRef) setFormData((s) => ({ ...s, ref: importedRef }));
        const importedReqDate = data.reqDate?.trim();
        if (importedReqDate) setFormData((s) => ({ ...s, reqDate: importedReqDate }));
        if (data.vesselId != null) setFormData((s) => ({ ...s, vesselId: String(data.vesselId) }));
        if (data.categoryIds?.length) setFormData((s) => ({ ...s, categoryIds: [...data.categoryIds] }));
        if (data.notes?.trim()) {
          setFormData((s) => ({
            ...s,
            notes: s.notes?.trim() ? `${s.notes.trim()}\n\n${data.notes}` : data.notes ?? '',
          }));
        }
        if (data.lines.length > 0) {
          setLines(
            data.lines.map((l) => ({
              key: `xlsx-${Date.now()}-${Math.random()}`,
              itemId: l.itemId != null ? String(l.itemId) : '',
              itemVcode: l.itemVcode || '',
              itemDescr: l.itemDescr || '',
              qty: l.qty != null && l.qty !== undefined ? String(l.qty) : '',
              itemNotes: l.itemNotes || '',
            })),
          );
        }
        const w = data.warnings?.length ? ` ${data.warnings.join(' ')}` : '';
        const summary =
          data.lineCount > 0
            ? `Imported ${data.lineCount} line(s) from Excel.`
            : 'No data rows found in the mapped line area (header fields may still have been applied).';
        showSuccess(`${summary}${w}`);
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: unknown } } };
        const raw = ax.response?.data?.message;
        const msg =
          typeof raw === 'string'
            ? raw
            : Array.isArray(raw)
              ? raw.join(' ')
              : err instanceof Error
                ? err.message
                : 'Excel import failed.';
        showError(msg);
      } finally {
        setImportExcelBusy(false);
      }
    },
    [showError, showSuccess],
  );

  const caseOptions = React.useMemo(() => {
    const rows = [
      { value: '', label: 'No case' },
      ...cases.map((c: { id: number; ref?: string | null }) => ({
        value: String(c.id),
        label: c.ref || `Case #${c.id}`,
      })),
    ];
    const sc = supplyCaseForPrefill;
    if (sc?.id != null && !rows.some((o) => o.value === String(sc.id))) {
      rows.push({ value: String(sc.id), label: sc.ref || `Case #${sc.id}` });
    }
    return rows;
  }, [cases, supplyCaseForPrefill]);

  const vesselOptions = React.useMemo(() => {
    const rows = [
      { value: '', label: 'No vessel' },
      ...vessels.map((v) => ({ value: String(v.id), label: v.vesselName || `Vessel #${v.id}` })),
    ];
    const v = supplyCaseForPrefill?.vessel;
    if (v?.id != null && !rows.some((o) => o.value === String(v.id))) {
      rows.push({ value: String(v.id), label: v.vesselName || `Vessel #${v.id}` });
    }
    return rows;
  }, [vessels, supplyCaseForPrefill]);
  const requisitionCaseCaption = React.useMemo(() => {
    if (!isEdit) return null;
    const caseIdN = Number(formData.caseId);
    if (!Number.isFinite(caseIdN) || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        existing?.spareCase ?? {
          id: caseIdN,
        },
        (iso?: string | null) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [isEdit, formData.caseId, existing?.spareCase]);
  const lockVesselField = React.useMemo(() => {
    const caseIdN = Number(formData.caseId);
    return lockCaseContext || (isEdit && Number.isFinite(caseIdN) && caseIdN > 0);
  }, [lockCaseContext, isEdit, formData.caseId]);

  const getItemOptionsForLine = React.useCallback(
    (line: LineForm) => {
      const options = [
        { value: '', label: 'Select item' },
        ...items.map((item) => ({
          value: String(item.id),
          label: formatRequisitionItemLabel(item.vendorCode, item.name, item.drawingNo),
        })),
      ];
      if (line.itemId && !options.some((o) => o.value === line.itemId)) {
        options.push({
          value: line.itemId,
          label: formatRequisitionItemLabel(line.itemVcode, line.itemDescr),
        });
      }
      return options;
    },
    [items],
  );

  const existingLineItemIdsExcludingTarget = React.useMemo(() => {
    const ids = new Set<string>();
    for (const line of lines) {
      if (line.key === pickerLineKey) continue;
      if (line.itemId) ids.add(line.itemId);
    }
    return ids;
  }, [lines, pickerLineKey]);

  const openItemPicker = (line: LineForm) => {
    setPickerLineKey(line.key);
  };

  const closeItemPicker = () => {
    setPickerLineKey(null);
  };

  const applyItemsToLines = React.useCallback(
    (picked: SpareItem[]) => {
      if (!pickerLineKey || picked.length === 0) return;
      setLines((prev) => {
        const targetIndex = prev.findIndex((line) => line.key === pickerLineKey);
        if (targetIndex < 0) return prev;
        const next = [...prev];
        const toLinePatch = (item: SpareItem): Partial<LineForm> => ({
          itemId: String(item.id),
          itemDescr: item.name || '',
          itemVcode: item.vendorCode || '',
        });
        next[targetIndex] = {
          ...next[targetIndex],
          ...toLinePatch(picked[0]),
        };
        const extraLines = picked.slice(1).map((item) => ({
          ...newLine(),
          ...toLinePatch(item),
        }));
        if (extraLines.length > 0) {
          next.splice(targetIndex + 1, 0, ...extraLines);
        }
        return next;
      });
    },
    [pickerLineKey],
  );

  const setLine = (key: string, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const onItemChange = (lineKey: string, itemId: string) => {
    if (!itemId) {
      setLine(lineKey, { itemId: '', itemDescr: '', itemVcode: '' });
      return;
    }
    const item = itemsById.get(itemId);
    setLine(lineKey, {
      itemId,
      itemDescr: item?.name || '',
      itemVcode: item?.vendorCode || '',
    });
  };

  const normalizedLines: SpareRequisitionLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescr: line.itemDescr || undefined,
        qty: asNumber(line.qty),
        itemNotes: line.itemNotes || undefined,
      }))
      .filter((line) => line.itemId || line.itemDescr || line.qty || line.itemNotes);
  }, [lines]);

  const requisitionHasCaseSelected = React.useMemo(() => {
    const raw = formData.caseId?.trim();
    if (!raw || !/^\d+$/.test(raw)) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0;
  }, [formData.caseId]);

  const submitRequisition = async (saveStatus: 'DRAFT' | 'SAVED') => {
    const missing: string[] = [];
    if (!formData.reqDate.trim()) missing.push('Date');
    if (!formData.ref.trim()) missing.push('Reference');
    if (!formData.vesselId.trim()) missing.push('Vessel');
    if (missing.length > 0) {
      showError(`Required: ${missing.join(', ')}`);
      return;
    }
    const caseIdNum = asNumber(formData.caseId);
    if (saveStatus === 'DRAFT' && caseIdNum != null && caseIdNum > 0) {
      showError(
        'Draft requisitions cannot be linked to a spare case. Clear the case or use Save to finalize the requisition.',
      );
      return;
    }
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        reqDate: formData.reqDate || undefined,
        caseId: asNumber(formData.caseId),
        vesselId: asNumber(formData.vesselId),
        categoryIds: formData.categoryIds,
        ref: formData.ref || undefined,
        refN: asNumber(formData.refN),
        notes: formData.notes || undefined,
        lines: normalizedLines,
        saveStatus,
      };
      if (
        !isEdit &&
        hasActiveRequisitionTemplate &&
        spareRequisitionRefPeekSnapshot.shouldOmitRefForServerAllocation(formData.ref)
      ) {
        delete payload.ref;
        delete payload.refN;
      }
      if (isEdit && requisitionId) {
        await updateSpareRequisition(requisitionId, payload);
      } else {
        await createSpareRequisition(payload);
      }
      goBack();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submitRequisition('SAVED');
      }}
    >
      <Card
        variant="outlined"
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5',
          'border shadow-sm',
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('-ml-2 gap-1.5 font-medium', colors.text)}
          onClick={goBack}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
          {isEdit && existing?.saveStatus ? (
            <span
              className={cn(
                'rounded-md px-2 py-0.5 text-xs font-medium',
                existing.saveStatus === 'DRAFT'
                  ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                  : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
              )}
            >
              {existing.saveStatus === 'DRAFT' ? 'Draft' : 'Saved'}
            </span>
          ) : null}
          <Button
            type="button"
            variant="outline"
            disabled={isSaving || loadingExisting || requisitionHasCaseSelected}
            loading={isSaving}
            size="default"
            title={
              requisitionHasCaseSelected
                ? 'Remove the spare case to save a draft, or use Save to link this finalized requisition to the case.'
                : undefined
            }
            onClick={() => void submitRequisition('DRAFT')}
          >
            Save draft
          </Button>
          <Button type="submit" disabled={isSaving || loadingExisting} loading={isSaving} size="default">
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Card>

      {requisitionCaseCaption ? (
        <Callout variant="success" title="Linked spare case">{requisitionCaseCaption}</Callout>
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Requisition details"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div
              className={cn(
                'grid w-full gap-4',
                '[grid-template-columns:repeat(auto-fit,minmax(min(100%,11rem),1fr))]',
              )}
            >
              <div className="min-w-0">
                <Label htmlFor="req-date" required>
                  Date
                </Label>
                <DateInput
                  id="req-date"
                  value={formData.reqDate}
                  onChange={(e) => setFormData((s) => ({ ...s, reqDate: e.target.value }))}
                  placeholder="Select date"
                  showFormatHint={false}
                />
              </div>
              <div className="min-w-0">
                <Label htmlFor="req-ref" required>
                  Reference
                </Label>
                <TextInput
                  id="req-ref"
                  value={formData.ref}
                  onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))}
                  placeholder={allowManualRequisitionRef ? 'Reference' : ''}
                  showClearButton={allowManualRequisitionRef}
                  readOnly={!allowManualRequisitionRef}
                  title={
                    allowManualRequisitionRef
                      ? undefined
                      : 'Reference is generated from the spare reference template (manual edit disabled for requisitions).'
                  }
                />
              </div>
              <div className="min-w-0">
                <Label htmlFor="req-vessel" required>
                  Vessel
                </Label>
                <Select
                  id="req-vessel"
                  name="vesselId"
                  value={formData.vesselId}
                  onChange={(e) => setFormData((s) => ({ ...s, vesselId: e.target.value }))}
                  options={vesselOptions}
                  placeholder="No vessel"
                  showClearButton={false}
                  disabled={lockVesselField}
                />
              </div>
              <div className="min-w-0 md:col-span-2">
                <Label htmlFor="req-categories">Categories</Label>
                <MultiSelect
                  id="req-categories"
                  options={categoryMultiOptions}
                  value={formData.categoryIds}
                  onChange={(v) =>
                    setFormData((s) => ({
                      ...s,
                      categoryIds: v.map((x) => Number(x)).filter((n) => !Number.isNaN(n)),
                    }))
                  }
                  placeholder="Select spare categories…"
                  maxDisplay={3}
                />
              </div>
              <div className="min-w-0 md:col-span-2">
                <Label htmlFor="req-notes">Notes</Label>
                <Textarea
                  id="req-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData((s) => ({ ...s, notes: e.target.value }))}
                  rows={2}
                  showClearButton={false}
                  className="min-h-[72px]"
                />
              </div>
            </div>
          </AccordionItem>
          <AccordionItem
            title="Lines"
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-8 w-8',
                  colors.textSecondary,
                  'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25',
                )}
                title="Delete all lines"
                aria-label="Delete all lines"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteAllLinesOpen(true);
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            }
          >
            <div
              className={cn(
                'mb-3 flex flex-col gap-2 rounded-lg border p-2 sm:mb-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1.5 sm:p-1.5 sm:pl-2.5',
                colors.border,
                colors.surface,
              )}
              title="Load or save line lists. Requisition header is not stored in a template."
            >
              <span
                className={cn(
                  'shrink-0 pl-0.5 text-[0.65rem] font-semibold uppercase leading-none tracking-wide',
                  colors.textSecondary,
                )}
              >
                Templates
              </span>
              <div className="min-w-0 flex-1 sm:max-w-sm">
                <Label htmlFor="requisition-line-template" className="sr-only">
                  Line template
                </Label>
                <Select
                  id="requisition-line-template"
                  name="lineTemplate"
                  value={templateApplyId}
                  onChange={(e) => setTemplateApplyId(e.target.value)}
                  options={templateSelectOptions}
                  placeholder="Saved template…"
                  showClearButton={false}
                  className="h-8 w-full text-sm"
                />
              </div>
              <div
                className="flex flex-wrap items-center gap-1.5 sm:shrink-0"
                role="group"
                aria-label="Template actions"
              >
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1"
                  disabled={!templateApplyId}
                  onClick={applyLineTemplate}
                >
                  <DocumentTextIcon className="h-3.5 w-3.5" />
                  Apply
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={() => {
                    setSaveTemplateName('');
                    setSaveTemplateOpen(true);
                  }}
                >
                  <FolderPlusIcon className="h-3.5 w-3.5" />
                  New…
                </Button>
              </div>
              <div
                className={cn('flex w-full sm:ml-auto sm:w-auto sm:shrink-0 sm:border-l sm:pl-2', colors.border)}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-full gap-1 sm:w-auto"
                  onClick={() => setLines((prev) => [...prev, newLine()])}
                  title="Add a blank line"
                  aria-label="Add line"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add line
                </Button>
              </div>
              <>
                <input
                  ref={excelImportInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="sr-only"
                  tabIndex={-1}
                  onChange={handleExcelImportChange}
                />
                <div
                  className={cn('flex w-full sm:w-auto sm:shrink-0 sm:border-l sm:pl-2', colors.border)}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-8 w-full gap-1 sm:w-auto"
                    disabled={importExcelBusy}
                    loading={importExcelBusy}
                    onClick={() => excelImportInputRef.current?.click()}
                    title="Merge data from an .xlsx into this form using the active REQUISITION template (Administration → Excel export templates). Replaces line rows when the file contains lines."
                  >
                    <UploadCloudIcon className="h-3.5 w-3.5" />
                    Import Excel
                  </Button>
                </div>
              </>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[40rem] w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="px-2 py-2 text-left text-xs font-medium">Item</th>
                    <th className="w-24 min-w-[6rem] max-w-[6rem] px-2 py-2 text-left text-xs font-medium">Qty</th>
                    <th className="w-[12rem] min-w-[12rem] max-w-[12rem] px-2 py-2 text-left text-xs font-medium">Notes</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.key} className="align-middle">
                      <td className="p-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            title="Find item"
                            aria-label="Find item"
                            className="h-10 w-10 shrink-0"
                            onClick={() => openItemPicker(line)}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.85-4.65a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </Button>
                          <Select
                            value={line.itemId}
                            onChange={(e) => onItemChange(line.key, e.target.value)}
                            options={getItemOptionsForLine(line)}
                            placeholder="Select item"
                            showClearButton={false}
                            className="min-w-0 flex-1 text-sm"
                          />
                        </div>
                      </td>
                      <td className="w-24 min-w-[6rem] max-w-[6rem] p-2 align-middle">
                        <NumberInput
                          size="sm"
                          value={line.qty}
                          onChange={(e) =>
                            setLine(line.key, {
                              qty: e.target.value === null || e.target.value === undefined ? '' : String(e.target.value),
                            })
                          }
                          showClearButton={false}
                          placeholder="Qty"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="w-[12rem] min-w-[12rem] max-w-[12rem] p-2 align-middle">
                        <TextInput
                          size="sm"
                          value={line.itemNotes}
                          onChange={(e) => setLine(line.key, { itemNotes: e.target.value })}
                          showClearButton={false}
                          placeholder="Notes"
                          className="h-10"
                        />
                      </td>
                      <td className="w-24 p-2 text-right align-middle">
                        <div className="inline-flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            title="View item details"
                            aria-label="View item details"
                            className="h-10 w-10"
                            disabled={!line.itemId}
                            onClick={() => {
                              const id = Number(line.itemId);
                              if (Number.isFinite(id) && id > 0) setViewItemId(id);
                            }}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            size="icon"
                            title={lines.length > 1 ? 'Remove line' : 'Clear line'}
                            aria-label={lines.length > 1 ? 'Remove line' : 'Clear line'}
                            className="h-10 w-10"
                            onClick={() =>
                              setLines((prev) => {
                                if (prev.length > 1) {
                                  return prev.filter((r) => r.key !== line.key);
                                }
                                return [newLine()];
                              })
                            }
                          >
                            <TrashIcon className="h-5 w-5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>

      <SpareItemPickerModal
        isOpen={pickerOpen}
        onClose={closeItemPicker}
        excludeItemIds={existingLineItemIdsExcludingTarget}
        onInsert={(items) => applyItemsToLines(items)}
      />

      <SpareItemDetailModal
        isOpen={viewItemId != null}
        itemId={viewItemId}
        onClose={() => setViewItemId(null)}
      />

      <Modal isOpen={deleteAllLinesOpen} onClose={() => setDeleteAllLinesOpen(false)} size="md" zIndex={120}>
        <ModalHeader>
          <ModalTitle>Delete all lines?</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <p className={cn('text-sm', colors.textSecondary)}>
            All {lines.length} line{lines.length === 1 ? '' : 's'} will be removed and replaced with a single empty row. You
            can still use Save draft or the main form actions as usual.
          </p>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteAllLinesOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => {
              setLines([newLine()]);
              setDeleteAllLinesOpen(false);
            }}
          >
            Delete all
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} size="md" zIndex={120}>
        <ModalHeader>
          <ModalTitle>Save as new line template</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tpl-name">Name</Label>
              <TextInput
                id="tpl-name"
                value={saveTemplateName}
                onChange={(e) => setSaveTemplateName(e.target.value)}
                placeholder="e.g. Monthly provisions"
                showClearButton
              />
            </div>
            <p className={cn('text-xs', colors.textMuted)}>
              Stores the current lines only (requisition header is not included).
            </p>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => setSaveTemplateOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={saveTemplateBusy}
            disabled={!saveTemplateName.trim() || normalizedLines.length === 0}
            onClick={async () => {
              const name = saveTemplateName.trim();
              if (!name || normalizedLines.length === 0) return;
              setSaveTemplateBusy(true);
              try {
                await createSpareRequisitionTemplate({
                  name,
                  lines: normalizedLines as Record<string, unknown>[],
                });
                setSaveTemplateOpen(false);
                setSaveTemplateName('');
              } finally {
                setSaveTemplateBusy(false);
              }
            }}
          >
            Save template
          </Button>
        </ModalFooter>
      </Modal>
    </form>
  );
};

export default Form;
